import React, { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import { cn } from '../lib/utils';

/**
 * SearchableSelect — a Select2-style searchable dropdown built on Shadcn Popover + Command.
 *
 * Props:
 *   value          — currently selected value ('' / null = nothing selected)
 *   onValueChange  — (newValue: string) => void
 *   options        — [{ value: string, label: string }]
 *   placeholder    — text shown when nothing is selected
 *   searchPlaceholder — text inside the search input
 *   disabled       — boolean
 *   className      — extra classes for the trigger button
 *   data-testid    — forwarded to the trigger button
 */
const SearchableSelect = ({
  value,
  onValueChange,
  options = [],
  placeholder = '--Select--',
  searchPlaceholder = 'Search...',
  disabled = false,
  className = '',
  'data-testid': testId,
}) => {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);
  // In trigger: show clean value text (no ISO codes); fallback to raw value for legacy data
  const triggerLabel = selected ? selected.value : (value || null);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId}
          className={cn(
            'flex h-10 w-full items-center justify-between rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-ibm ring-offset-background',
            'hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent-blue focus:ring-offset-1',
            'disabled:cursor-not-allowed disabled:opacity-50',
            !triggerLabel && 'text-muted-foreground',
            className
          )}
        >
          <span className="truncate">{triggerLabel ?? placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        className="p-0"
        align="start"
        style={{ width: 'var(--radix-popover-trigger-width)' }}
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} className="h-9" />
          <CommandList className="max-h-60">
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt.value === '' ? '__empty__' : opt.value}
                  value={opt.label}
                  onSelect={() => {
                    onValueChange(opt.value);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === opt.value ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {opt.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default SearchableSelect;
